"use client"

import type React from "react"
import { useEffect, useRef, useState } from "react"

import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Avatar from "@mui/material/Avatar";

import type { Term } from "../types"
import { saveTermsBulk } from "../../api/admin"
import DoneIcon from '@mui/icons-material/Done';

interface RightPanelProps {
  terms: Term[]
  selectedTerm: Term | null
  onTermSelect: (term: Term) => void
  onTermDelete: (term: Term) => void
  onTermUpdate: (term: Term) => void
  onTermAdd: () => void
  onSave: () => void
  attachmentId?: string
}

const RightPanel: React.FC<RightPanelProps> = ({ terms, selectedTerm, onTermSelect, onTermDelete, onTermUpdate, onTermAdd, onSave, attachmentId }) => {
  const [synonymInput, setSynonymInput] = useState("")
  const [termInput, setTermInput] = useState("")
  const pendingNewTermNameRef = useRef<string | null>(null)
  const prevSelectedIdRef = useRef<string | null>(selectedTerm?.termsId ?? null)
  const [saving, setSaving] = useState<boolean>(false)
  const [isDirty, setIsDirty] = useState<boolean>(false)

  const handleSynonymAdd = () => {
    if (selectedTerm && synonymInput.trim()) {
      const updatedTerm = { ...selectedTerm, altTerms: [...(selectedTerm.altTerms || []), synonymInput.trim()], status: "request" }
      onTermUpdate(updatedTerm)
      setSynonymInput("")
      setIsDirty(true)
    }
  }

  const handleTermAddFromInput = () => {
    const name = termInput.trim()
    if (!name) return
    // 부모가 새 용어를 생성하고 선택하도록 요청
    pendingNewTermNameRef.current = name
    onTermAdd()
    setIsDirty(true)
  }

  const handleSaveTerm = async () => {
    if (!attachmentId) return
    try {
      setSaving(true)
      await saveTermsBulk({
        attachmentId,
        terms: terms.map(t => ({
          termsId: t.termsId,
          terms: t.terms,
          altTerms: t.altTerms ?? null,
          descryption: t.descryption ?? null,
          status: (t.status ?? null) as string | null,
        })),
      })
      // 모든 terms의 상태값을 "registered"로 변경
      terms.forEach(t => {
        t.status = "registered"
      })
      setIsDirty(false)
    } finally {
      setSaving(false)
    }
  }

  const handleSynonymRemove = (index: number) => {
    if (selectedTerm) {
      const updatedTerm = { ...selectedTerm, altTerms: (selectedTerm.altTerms || []).filter((_, i) => i !== index), status: "request" }
      onTermUpdate(updatedTerm)
      setIsDirty(true)
    }
  }

  const handleDescriptionChange = (descryption: string) => {
    if (selectedTerm) {
      const updatedTerm = { ...selectedTerm, descryption, status: "request" }
      onTermUpdate(updatedTerm)
      setIsDirty(true)
    }
  }

  const handleTermDeleteLocal = (term: Term) => {
    onTermDelete(term)
    setIsDirty(true)
  }

  // 새 용어 생성 직후 선택이 변경되면, 대기 중인 이름으로 업데이트
  useEffect(() => {
    const prevId = prevSelectedIdRef.current
    const currId = selectedTerm?.termsId ?? null
    if (pendingNewTermNameRef.current && currId && currId !== prevId && selectedTerm) {
      const newName = pendingNewTermNameRef.current
      onTermUpdate({ ...selectedTerm, terms: newName })
      pendingNewTermNameRef.current = null
      setTermInput("")
    }
    prevSelectedIdRef.current = currId
  }, [selectedTerm, onTermUpdate])

  // 파일(attachmentId) 변경 시 입력값 및 더티 상태 초기화
  useEffect(() => {
    setIsDirty(false)
    setTermInput("")
    setSynonymInput("")
    pendingNewTermNameRef.current = null
  }, [attachmentId])

  return (
    <Paper sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "700"  }}>
          용어 목록
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <TextField
            size="small"
            placeholder="용어 입력"
            value={termInput}
            onChange={(e) => setTermInput(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleTermAddFromInput()
              }
            }}
            sx={{ flex: 1 }}
          />
          <Button variant="outlined" onClick={handleTermAddFromInput} disabled={!termInput.trim()}>
            추가
          </Button>
        </Stack>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {terms.map((term) => (
            <Chip
              key={term.termsId}
              label={term.terms}
              onClick={() => onTermSelect(term)}
              onDelete={() => handleTermDeleteLocal(term)}
              // color={term.status === "registered" ? "primary" : "default"}
              variant={selectedTerm?.termsId !== term.termsId ? "filled" : "outlined"}
              sx={{ bgcolor: selectedTerm?.termsId === term.termsId ? "primary.light" : "grey.200"
                  , color: selectedTerm?.termsId !== term.termsId ? "black": "white"
                  , "&:hover": {
                    color: "primary.main"
                    , border: "1px solid rgb(71, 73, 76)"
                  }
                }}
              avatar={term.status === "registered" ?
                <Avatar sx={{ bgcolor: "transparent" }}>
                  <DoneIcon sx={{ color: selectedTerm?.termsId !== term.termsId ? "primary.main" : "white"
                    , "&:hover": {
                      color: "primary.main"
                    }
                  }}
                  />
                </Avatar> : undefined}
            />
          ))}
        </Box>
      </Box>

      {selectedTerm && (
        <Box sx={{ p: 2, overflow: "auto" }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: "700"  }}>
              "{selectedTerm.terms}" 혼용어
            </Typography>

            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <TextField
                size="small"
                placeholder="혼용어 입력"
                value={synonymInput}
                onChange={(e) => setSynonymInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleSynonymAdd()
                  }
                }}
                sx={{ flex: 1 }}
              />
              <Button variant="outlined" onClick={handleSynonymAdd} disabled={!synonymInput.trim()}>
                추가
              </Button>
            </Stack>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {(selectedTerm.altTerms || []).map((synonym, index) => (
                <Chip key={index} label={synonym} onDelete={() => handleSynonymRemove(index)} size="small" />
              ))}
            </Box>
          </Box>

          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: "700" }}>
              "{selectedTerm.terms}" 용어 설명
            </Typography>
            <TextField
              multiline
              rows={6}
              fullWidth
              value={selectedTerm.descryption ?? ""}
              onChange={(e) => handleDescriptionChange(e.target.value)}
              placeholder="용어에 대한 설명을 입력하세요..."
            />
          </Box>
        </Box>
      )}

      {/* <Box sx={{ p: 2, overflow: "auto" }}>
        <Button sx={{ flex: 1, p: 2, maxHeight: "45px" }} variant="contained" fullWidth onClick={handleSaveTerm} disabled={!attachmentId || saving || !isDirty}>
          {saving ? '저장 중...' : '임시 저장'}
        </Button>
      </Box> */}

      {!selectedTerm && (
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography color="text.secondary">용어를 선택하세요</Typography>
        </Box>
      )}
    </Paper>
  )
}

export default RightPanel